import { Card, Chip, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";

import { relativeTime } from "@/lib/relativeTime";

import { salesApi } from "../client.js";

/**
 * The two-agent strip: the Lead Generator vs the Harvester, what each does, and
 * a live Working / Standing by / Paused state derived from the agent mode.
 * Lives on the Status tab (the detailed "which bot is running" — the compact
 * always-visible version is `<AgentWorkingNow>` in the shell header). The agent
 * names match the toggle + the AgentWorkingNow strip. Self-fetching.
 */
export function TwoAgentStrip(): JSX.Element {
  const ctl = useQuery({
    queryKey: ["sales", "agent-control"],
    queryFn: salesApi.getAgentControl,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
  const mode = ctl.data?.mode ?? "leads";
  const paused = ctl.data?.is_paused ?? false;
  const leadRunning = !paused && (mode === "leads" || mode === "both");
  const harvestRunning = !paused && (mode === "contacts" || mode === "both");
  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap>
      <AgentCard
        title="Lead Generator"
        desc="Signal-driven — hunts buying triggers, then qualifies & scores leads into the pipeline."
        running={leadRunning}
        paused={paused}
      />
      <AgentCard
        title="Harvester"
        desc="Blindly collects deep contacts at every facility in the target industries — fills the Harvester directory."
        running={harvestRunning}
        paused={paused}
        emphasis
      />
    </Stack>
  );
}

function AgentCard({
  title, desc, running, paused, emphasis,
}: {
  title: string;
  desc: string;
  running: boolean;
  paused: boolean;
  emphasis?: boolean;
}): JSX.Element {
  const state = paused ? "Paused" : running ? "Working" : "Standing by";
  const color = paused ? "warning" : running ? "success" : "default";
  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        p: 2,
        borderColor: running ? "success.main" : "divider",
        bgcolor: emphasis && running ? "surfaceSoft" : "transparent",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Chip label={state} size="small" color={color} variant={running ? "filled" : "outlined"} sx={{ borderRadius: 1, fontWeight: 500 }} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{desc}</Typography>
    </Card>
  );
}

const _SCOPE_LABELS: Record<string, string> = {
  harvest_enumerate: "Enumerate",
  harvest_dig: "Dig",
  harvest_dedup: "Dedup",
  harvest_consume: "Promote",
};

/** The harvester's live "thinking" feed — its recent enumerate / dig /
 * deep-search / dedup / promote log lines. Polls every 8s. Self-fetching.
 *
 * `maxHeight` lets the Brain tab render it tall (its primary "consciousness"
 * surface in contacts mode) while the compact callers default to 240px. */
export function HarvesterActivity({
  heading = "Harvester activity — what the contact agent is doing",
  maxHeight = 240,
}: {
  heading?: string;
  maxHeight?: number | string;
} = {}): JSX.Element {
  const activity = useQuery({
    queryKey: ["sales", "harvest", "activity"],
    queryFn: salesApi.getHarvestActivity,
    refetchInterval: 8_000,
  });
  const items = activity.data?.items ?? [];
  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {heading}
      </Typography>
      {activity.isLoading && items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No harvester activity yet. Switch the agent to <strong>Contacts</strong> mode
          (top-right) and it'll start enumerating facilities and digging their contacts here.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={{ maxHeight, overflowY: "auto", fontFamily: "monospace", fontSize: 13 }}>
          {items.map((it) => (
            <Stack key={it.id} direction="row" spacing={1} alignItems="baseline">
              <Chip
                label={_SCOPE_LABELS[it.scope] ?? it.scope}
                size="small"
                sx={{ borderRadius: 1, height: 20, fontSize: 11, minWidth: 72 }}
              />
              <Typography
                component="span"
                sx={{
                  flex: 1, fontFamily: "monospace", fontSize: 13,
                  color: it.level === "WARNING" || it.level === "ERROR" ? "warning.main" : "text.primary",
                }}
              >
                {it.message}
              </Typography>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {relativeTime(it.ts)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Card>
  );
}
